import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { heroSlides } from "@/mocks/heroSlides";
import { films } from "@/mocks/films";
import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import LatestFilms from "@/pages/home/components/LatestFilms";
import NewsPreview from "@/pages/home/components/NewsPreview";
import RankingList from "@/pages/home/components/RankingList";

export default function Home() {
  usePageMeta({
    title: "三级片电影大全 - 最新三级片影视资讯与作品介绍",
    description:
      "三级片网站为您提供丰富的三级片电影资源和资讯，涵盖经典与近期作品，帮助用户全面了解三级片影片内容和相关信息。",
    keywords: "三级片,三级片电影,三级片介绍,三级片电影在线观看,三级片资讯,三级片作品。",
  });

  const latestFilms = [...films].sort((a, b) => b.year - a.year).slice(0, 8);
  const rankingFilms = [...films].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const latestNews = [...newsList].sort((a, b) => b.date.localeCompare(a.date));

  const stats = [
    { value: `${films.length}`, label: "收录影片资料" },
    { value: `${categories.length}`, label: "影片类型索引" },
    { value: `${newsList.length}`, label: "行业资讯文章" },
    { value: "0", label: "在线播放入口" },
  ];

  return (
    <SiteLayout>
      <div className="rise-in">
        <section className="mb-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-accent-600">
                <span className="inline-block h-[2px] w-6 bg-accent-500" />
                Category III Film Archive
              </p>
              <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[32px]">
                三级片电影大全
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                整理经典与近期三级片作品资料，按类型、地区与年代分类索引，并提供行业资讯与影片背景介绍，帮助你在观看之前先了解作品本身。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
              <span className="rounded-full border border-background-300 px-2.5 py-1">影视资料</span>
              <span className="rounded-full border border-background-300 px-2.5 py-1">分类索引</span>
              <span className="rounded-full border border-background-300 px-2.5 py-1">行业资讯</span>
            </div>
          </div>
        </section>

        <HeroCarousel slides={heroSlides} />

        <section className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="bg-background-50 px-4 py-3.5">
              <p className="font-heading text-xl font-bold text-primary-700 md:text-2xl">{item.value}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{item.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-10">
          <SectionHeading
            eyebrow="Browse by genre"
            title="分类快捷入口"
            description="按类型进入影片索引，快速找到符合口味的作品。"
            moreLabel="浏览全部分类"
            moreLink="/category/drama"
          />
          <CategoryGrid />
        </section>

        <section className="mt-12">
          <SectionHeading
            eyebrow="Latest additions"
            title="最新影片推荐"
            description="近期更新资料的作品，按上映年份排序。"
            moreLabel="查看分类页"
            moreLink="/category/drama"
          />
          <LatestFilms films={latestFilms} />
        </section>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <section>
            <SectionHeading
              eyebrow="Industry news"
              title="近期资讯"
              description="行业观察、影片动态与幕后访谈，按发布时间倒序更新。"
              moreLabel="全部资讯"
              moreLink="/news"
            />
            <NewsPreview featured={latestNews[0]} items={latestNews.slice(1, 5)} />
          </section>
          <section>
            <div className="mb-6 flex items-end justify-between">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">热度榜</h2>
              <Link
                to="/category/crime"
                className="cursor-pointer text-sm text-foreground-600 transition-colors hover:text-primary-700"
              >
                更多
                <i className="ri-arrow-right-line ml-1" />
              </Link>
            </div>
            <RankingList films={rankingFilms} />
          </section>
        </div>

        <section className="mt-12 overflow-hidden rounded-lg border border-background-200 bg-secondary-50">
          <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between md:p-6">
            <div>
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-shield-check-line text-secondary-600" />
                关于本站内容
              </h2>
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-foreground-600 md:text-sm">
                本站为影视资料与资讯展示站点，站内影片名称、主创人员、评分及资讯内容均为虚构演示数据，不对应任何真实作品。本站不提供在线播放、下载、付费或会员服务。
              </p>
            </div>
            <Link
              to="/news"
              className="flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              阅读资讯
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}